import React, {useCallback, useEffect, useState} from 'react';
import {Button, Box, Grid} from '@mui/material';
import {Filter, FilterDocumentType, FilterLocation} from '@/types/filter';
import {SelectChangeEvent} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {
  Location as LocationType,
  LocationExtended
} from '@/schemas/sub-schemas';
import {useDispatch} from 'react-redux';
import {updateFilter} from '@/redux-store/slices/filter';
import {useAppSelector} from '@/redux-store/store';
import SportFilter from './filters/sport';
import LocationFilter from './filters/location';
import PaidFilter from './filters/paid';
import OnlineFilter from './filters/online';
import AgeGroupFilter from './filters/age-group';
import GroupFilter from './filters/group';
import TimeFilter from './filters/date-time/time';
import DateFilter from './filters/date-time/date';
import WeekdaysFilter from './filters/date-time/weekdays';
import MyDocumentsFilter from './filters/my-documents';
import CompanyFilter from './filters/companies';

interface FilterFormProps {
  filterType: FilterDocumentType;
  onApply: (filterData: Filter, hideFilter?: boolean) => void;
  hideFilterOnApply?: boolean;
}

const FilterForm: React.FC<FilterFormProps> = ({
  filterType,
  onApply,
  hideFilterOnApply
}) => {
  const filter = useAppSelector((state) => state.filter);
  const {isAuthenticated} = useAppSelector((state) => state.auth);
  const userInfos = useAppSelector((state) => state.userInfos.userInfos);
  const myGroups = useAppSelector((state) => state.groups.myGroups);
  const userRole = useAppSelector((state) => state.auth.keycloakUser?.userRole);
  const globalFilter = useAppSelector((state) => state.filter[filterType]);

  const [localFilter, setLocalFilter] = useState<Filter>(globalFilter);
  const [validationError, setValidationError] = useState(false);

  const isTrainer = userRole && userRole === 'TRAINER';

  const dispatch = useDispatch();

  const {t} = useTranslation();

  const handleApply = useCallback(
    (data?: Partial<Filter>, reset?: boolean) => {
      const dataToApply = data || localFilter;
      dispatch(updateFilter({type: filterType, data: dataToApply}));
      const hideFilter = reset ? false : hideFilterOnApply;
      onApply(dataToApply, hideFilter);
    },
    [dispatch, filterType, localFilter, hideFilterOnApply, onApply]
  );

  const handleSportChange = useCallback((newSportId: string | string[]) => {
    const sportsArray = Array.isArray(newSportId) ? newSportId : [newSportId];
    setLocalFilter((prev) => ({
      ...prev,
      sports: sportsArray
    }));
  }, []);

  const handleLocationChange = useCallback(
    (newLocation: LocationType | LocationExtended | null) => {
      if (!newLocation) {
        setLocalFilter((prev) => ({
          ...prev,
          location: undefined,
          distance: 15000,
          locationLabel: ''
        }));
        return;
      }
      const filterLocation: FilterLocation = {
        coordinates: [newLocation.lng, newLocation.lat]
      };
      setLocalFilter((prev) => ({
        ...prev,
        location: filterLocation,
        distance: 'distance' in newLocation ? newLocation.distance : 15000,
        locationLabel: newLocation.label || ''
      }));
    },
    []
  );

  const handleMyFilterChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const value = event.target.value;
      let newData: Partial<Filter> = {};

      if (filterType === 'group') {
        newData = {myTeams: value === 'my'};
      } else if (filterType === 'event') {
        newData = {
          pastEvents: value === 'past',
          participants: value === 'my' ? [userInfos._id] : []
        };
        if (value !== 'past') {
          newData.pastEvents = false;
        }
      }

      setLocalFilter((prev) => ({
        ...prev,
        ...newData
      }));
    },
    [filterType, userInfos._id]
  );

  const handleGetCurrentMyFilterValue = useCallback(() => {
    switch (filterType) {
      case 'group':
        return localFilter.myTeams ? 'my' : 'all';
      case 'event':
        if (localFilter.pastEvents) {
          return 'past';
        }
        if (localFilter.participants?.includes(userInfos._id)) {
          return 'my';
        }
        return 'all';
      default:
        return 'all';
    }
  }, [
    filterType,
    localFilter.myTeams,
    localFilter.participants,
    localFilter.pastEvents,
    userInfos._id
  ]);

  const handlePaidChange = useCallback((event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    let paidValue: boolean | null = null;
    if (value === 'paid') {
      paidValue = true;
    } else if (value === 'free') {
      paidValue = false;
    }
    setLocalFilter((prev) => ({
      ...prev,
      isPaid: paidValue
    }));
  }, []);

  const handleOnlineChange = useCallback((event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    let onlineValue: boolean | null = null;
    if (value === 'online') {
      onlineValue = true;
    } else if (value === 'offline') {
      onlineValue = false;
    }
    setLocalFilter((prev) => ({
      ...prev,
      online: onlineValue
    }));
  }, []);

  const handleAgeGroupChange = useCallback((selected: string[]) => {
    setLocalFilter((prev) => ({
      ...prev,
      ageGroups: selected
    }));
  }, []);

  const handleGroupChange = useCallback((selected: string) => {
    setLocalFilter((prev) => ({
      ...prev,
      group: selected
    }));
  }, []);

  const handleDateChange = useCallback((date: Date) => {
    setLocalFilter((prev) => ({
      ...prev,
      start: date
    }));
  }, []);

  const handleTimeChange = useCallback(
    (field: 'startTime' | 'endTime', time: string) => {
      setLocalFilter((prev) => ({
        ...prev,
        [field]: time
      }));
    },
    []
  );

  const handleWeekdaysChange = useCallback((selected: number[]) => {
    setLocalFilter((prev) => ({
      ...prev,
      weekdays: selected
    }));
  }, []);

  const handleCompaniesChange = useCallback((selected: string[]) => {
    setLocalFilter((prev) => ({
      ...prev,
      companies: selected
    }));
  }, []);

  const handleValidationErrorChange = useCallback((error: boolean) => {
    setValidationError(error);
  }, []);

  useEffect(() => {
    setLocalFilter(globalFilter);
  }, [globalFilter]);

  return (
    <Box
      sx={{
        marginTop: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          width: '100%'
        }}
      >
        {isTrainer && (
          <CompanyFilter
            userData={userInfos}
            selectedCompanies={localFilter.companies || null}
            onCompaniesChange={handleCompaniesChange}
          />
        )}
        <SportFilter
          sports={localFilter.sports || []}
          onSportChange={handleSportChange}
        />

        {filterType !== 'onDemandEvent' && (
          <Grid container spacing={isAuthenticated ? 2 : 0}>
            <Grid item xs={12} sm={isAuthenticated ? 6 : 12}>
              {isAuthenticated && (
                <MyDocumentsFilter
                  filterType={filterType}
                  currentFilterValue={handleGetCurrentMyFilterValue()}
                  onFilterChange={handleMyFilterChange}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={isAuthenticated ? 6 : 12}>
              <LocationFilter
                filterType={filterType}
                filterLocation={localFilter.location as FilterLocation}
                filterLocationLabel={localFilter.locationLabel || undefined}
                onLocationChange={handleLocationChange}
              />
            </Grid>
          </Grid>
        )}

        {filterType === 'event' && (
          <>
            <Box display="flex" gap={2}>
              <DateFilter
                startDate={localFilter.start || null}
                onChangeDate={handleDateChange}
                pastEvents={!!filter.event.pastEvents}
              />
              <WeekdaysFilter
                onWeekdaysChange={handleWeekdaysChange}
                selectedWeekdays={localFilter.weekdays || undefined}
                onErrorChange={handleValidationErrorChange}
              />
            </Box>
            <TimeFilter
              startTime={localFilter.startTime || null}
              endTime={localFilter.endTime || null}
              onChangeTime={handleTimeChange}
              onErrorChange={handleValidationErrorChange}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <PaidFilter
                  onPaidChange={handlePaidChange}
                  currentPaidValue={localFilter.isPaid}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <OnlineFilter
                  onOnlineChange={handleOnlineChange}
                  currentOnlineValue={localFilter.online}
                />
              </Grid>
            </Grid>
          </>
        )}
        <Grid
          container
          spacing={
            isAuthenticated && myGroups.length > 0 && filterType === 'event'
              ? 2
              : 0
          }
        >
          <Grid
            item
            xs={12}
            sm={
              isAuthenticated && myGroups.length > 0 && filterType === 'event'
                ? 6
                : 12
            }
          >
            {(filterType === 'event' || filterType === 'onDemandEvent') && (
              <AgeGroupFilter
                selectedAgeGroups={localFilter.ageGroups || []}
                onAgeGroupChange={handleAgeGroupChange}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            {isAuthenticated &&
              myGroups.length > 0 &&
              filterType === 'event' && (
                <GroupFilter
                  onGroupChange={handleGroupChange}
                  selectedGroup={localFilter.group || null}
                  myGroups={myGroups}
                />
              )}
          </Grid>
        </Grid>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            flexDirection: {xs: 'column', sm: 'row'}
          }}
        >
          <Button
            variant="contained"
            color="secondary"
            onClick={() => handleApply()}
            disabled={validationError}
            sx={{
              width: '50%'
            }}
          >
            {t('apply_filter')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default FilterForm;
